import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import KanbanBoard from './components/KanbanBoard';
import PipelineTable from './components/PipelineTable';
import ProcurementSection from './components/ProcurementSection';
import { DEALS, STAGES, SECTORS } from './components/pipelineData';
import ToastNotification from '../spv-dashboard/components/ToastNotification';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import { ROUTES } from 'config/navConfig';

const REGIONS = [
  'North East England', 'West Midlands', 'South East England', 'South West England',
  'Scotland', 'Northern Ireland', 'Yorkshire', 'East Midlands', 'London',
  'UK', 'Europe', 'North America', 'Africa', 'Asia-Pacific',
];

export default function PipelinePage() {
  const navigate = useNavigate();
  const { role, activeContext } = useRole();
  const isReadOnly = role === ROLES.IC_MEMBER;
  const isDealOriginator = role === ROLES.DEAL_ORIGINATOR;
  const [deals, setDeals] = useState(DEALS);
  const [viewMode, setViewMode] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now()?.toString();
    setToasts((prev) => [...prev, { ...toast, id }]);
    setTimeout(() => {
      setToasts((prev) => prev?.filter((t) => t?.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const handleMoveStage = useCallback((dealId, newStageId) => {
    setDeals((prev) =>
      prev?.map((d) => d?.id === dealId ? { ...d, stage: newStageId, daysInStage: 0 } : d)
    );
  }, []);

  const handleToggleFlag = useCallback((dealId) => {
    setDeals((prev) =>
      prev?.map((d) => d?.id === dealId ? { ...d, flagged: !d?.flagged } : d)
    );
  }, []);

  const handleAddDeal = useCallback((stageId) => {
    const stageName = STAGES?.find((s) => s?.id === stageId)?.label;
    addToast({ type: 'success', title: 'Deal added', message: 'New deal added to ' + stageName });
  }, [addToast]);

  const handleAddDealHeader = () => {
    addToast({ type: 'success', title: 'Deal added', message: 'New deal has been added to the pipeline.' });
  };

  const filteredDeals = useMemo(() => {
    let result = deals;
    if (stageFilter !== 'all') {
      result = result?.filter((d) => d?.stage === stageFilter);
    }
    if (sectorFilter !== 'all') {
      result = result?.filter((d) => d?.sector === sectorFilter);
    }
    if (regionFilter !== 'all') {
      result = result?.filter((d) => d?.region === regionFilter);
    }
    if (assignedFilter !== 'all') {
      result = result?.filter((d) => d?.assignedName === assignedFilter);
    }
    if (flaggedOnly) {
      result = result?.filter((d) => d?.flagged);
    }
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter(
        (d) =>
          d?.name?.toLowerCase()?.includes(q) ||
          d?.region?.toLowerCase()?.includes(q) ||
          d?.assignedName?.toLowerCase()?.includes(q)
      );
    }
    if (isDealOriginator) {
      result = result?.filter((d) => d?.originatorContext === activeContext?.id);
    }
    return result;
  }, [deals, stageFilter, sectorFilter, regionFilter, assignedFilter, flaggedOnly, searchQuery, isDealOriginator, activeContext]);

  const totalDeals = deals?.length;
  const totalValue = deals?.reduce((sum, d) => sum + (d?.ticketSize || 0), 0);
  const flaggedCount = deals?.filter((d) => d?.flagged)?.length;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Sticky page header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div>
            <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>Pipeline</h1>
            <p className="font-caption text-muted-foreground text-sm mt-0.5">
              Deal flow across all stages — Sample data, for demonstration purposes only
            </p>
          </div>
          <div className="flex items-center gap-5 bg-card border border-border rounded-xl px-5 py-3">
            <div className="text-center">
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Total Deals</p>
              <p className="font-data text-xl font-semibold text-foreground">{totalDeals}</p>
            </div>
            <div className="w-px h-10 bg-border" />
            <div className="text-center">
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Pipeline Value</p>
              <p className="font-data text-xl font-semibold text-foreground">{String.fromCharCode(163)}{totalValue}m</p>
            </div>
            <div className="w-px h-10 bg-border" />
            <div className="text-center">
              <p className="font-caption text-xs text-muted-foreground mb-0.5">Flagged</p>
              <p className="font-data text-xl font-semibold" style={{ color: 'var(--color-warning)' }}>{flaggedCount}</p>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-10 lg:px-12 py-6">
          {/* IC Member read-only banner */}
          {isReadOnly && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-5 border"
              style={{ background: 'rgba(99,102,241,0.06)', borderColor: 'rgba(99,102,241,0.25)' }}>
              <Icon name="Eye" size={15} color="#6366f1" />
              <span className="font-caption text-sm font-medium" style={{ color: '#6366f1' }}>IC Member view</span>
              <span className="font-caption text-sm text-muted-foreground">— Pipeline is read-only. Deal management actions are restricted.</span>
            </div>
          )}

          {/* Deal Originator read-only banner */}
          {isDealOriginator && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-5 border"
              style={{ background: 'rgba(20,184,166,0.06)', borderColor: 'rgba(20,184,166,0.25)' }}>
              <Icon name="Eye" size={15} color="#14b8a6" />
              <span className="font-caption text-sm font-medium" style={{ color: '#14b8a6' }}>Deal Originator view</span>
              <span className="font-caption text-sm text-muted-foreground">— Showing your submitted deals only. Pipeline management is read-only.</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <div className="relative flex-1 min-w-48 max-w-72">
              <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search deals…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2 font-caption text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
              />
            </div>

            {!isReadOnly && !isDealOriginator && (
              <button
                onClick={handleAddDealHeader}
                className="flex items-center gap-2 px-4 py-2 text-white font-caption text-sm font-semibold rounded-lg transition-colors"
                style={{ background: 'var(--color-primary)' }}
              >
                <Icon name="Plus" size={16} />
                Add Deal
              </button>
            )}
            {isDealOriginator && (
              <button
                onClick={() => navigate(ROUTES.dealSubmission)}
                className="flex items-center gap-2 px-4 py-2 text-white font-caption text-sm font-semibold rounded-lg transition-colors"
                style={{ background: 'var(--color-primary)' }}
              >
                <Icon name="Send" size={16} />
                Submit a Deal
              </button>
            )}

            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e?.target?.value)}
              className="px-3 py-2 font-caption text-sm bg-card border border-border rounded-lg focus:outline-none text-foreground"
            >
              <option value="all">All Stages</option>
              {STAGES?.map((s) => (
                <option key={s?.id} value={s?.id}>{s?.label}</option>
              ))}
            </select>

            <select
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e?.target?.value)}
              className="px-3 py-2 font-caption text-sm bg-card border border-border rounded-lg focus:outline-none text-foreground"
            >
              <option value="all">All Sectors</option>
              {SECTORS?.map((s) => (
                <option key={s?.id} value={s?.id}>{s?.label}</option>
              ))}
            </select>

            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e?.target?.value)}
              className="px-3 py-2 font-caption text-sm bg-card border border-border rounded-lg focus:outline-none text-foreground"
            >
              <option value="all">All Regions</option>
              {[...new Set(deals?.map((d) => d?.region))]?.sort()?.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>

            <select
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e?.target?.value)}
              className="px-3 py-2 font-caption text-sm bg-card border border-border rounded-lg focus:outline-none text-foreground"
            >
              <option value="all">All Assigned</option>
              {[...new Set(deals?.map((d) => d?.assignedName))]?.sort()?.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <button
              onClick={() => setFlaggedOnly((p) => !p)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-caption text-sm font-medium border transition-all"
              style={flaggedOnly
                ? { background: 'rgba(217,119,6,0.08)', borderColor: 'rgba(217,119,6,0.4)', color: 'var(--color-warning)' }
                : { background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)' }
              }
            >
              <Icon name="Flag" size={14} color={flaggedOnly ? 'var(--color-warning)' : 'var(--color-muted-foreground)'} />
              Flagged Only
            </button>

            <div className="flex items-center bg-card border border-border rounded-lg p-1 ml-auto">
              <button
                onClick={() => setViewMode('kanban')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-caption text-sm font-medium transition-all"
                style={viewMode === 'kanban'
                  ? { background: 'var(--color-primary)', color: 'white' }
                  : { color: 'var(--color-muted-foreground)' }
                }
              >
                <Icon name="LayoutDashboard" size={15} />
                Kanban
              </button>
              <button
                onClick={() => setViewMode('table')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-caption text-sm font-medium transition-all"
                style={viewMode === 'table'
                  ? { background: 'var(--color-primary)', color: 'white' }
                  : { color: 'var(--color-muted-foreground)' }
                }
              >
                <Icon name="Table" size={15} />
                Table
              </button>
            </div>
          </div>

          {(stageFilter !== 'all' || sectorFilter !== 'all' || regionFilter !== 'all' || assignedFilter !== 'all' || flaggedOnly || searchQuery) && (
            <div className="flex items-center gap-2 mb-4">
              <span className="font-caption text-xs text-muted-foreground">Showing {filteredDeals?.length} of {totalDeals} deals</span>
              <button
                onClick={() => { setStageFilter('all'); setSectorFilter('all'); setRegionFilter('all'); setAssignedFilter('all'); setFlaggedOnly(false); setSearchQuery(''); }}
                className="font-caption text-xs font-medium flex items-center gap-1 hover:text-foreground transition-colors"
                style={{ color: 'var(--color-primary)' }}
              >
                <Icon name="X" size={12} />
                Clear filters
              </button>
            </div>
          )}

          {/* Main view */}
          <div style={{ transition: 'opacity 0.2s ease' }}>
            {viewMode === 'kanban' ? (
              <KanbanBoard
                deals={filteredDeals}
                onMoveStage={handleMoveStage}
                onToggleFlag={handleToggleFlag}
                onAddToast={addToast}
                onAddDeal={handleAddDeal}
              />
            ) : (
              <PipelineTable
                deals={filteredDeals}
                onMoveStage={handleMoveStage}
                onToggleFlag={handleToggleFlag}
                onAddToast={addToast}
              />
            )}
          </div>

          {/* Procurement section */}
          <ProcurementSection userRole={role} onAddToast={addToast} />
        </div>
      </div>

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 z-50 space-y-2">
        {toasts?.map((toast) => (
          <ToastNotification
            key={toast?.id}
            type={toast?.type}
            title={toast?.title}
            message={toast?.message}
            onDismiss={() => dismissToast(toast?.id)}
          />
        ))}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </AppLayout>
  );
}
